<template lang="pug">
div
  v-container.pa-8(fluid)
    div.d-flex.justify-center.mt-16(v-if="isFetching")
      v-progress-circular(:width="2" :size="50" color="primary" indeterminate)

    template(v-else-if="stockTake")
      div.d-flex.align-center
        .heading-4.font-semibold.neutral700--text {{ stockTake.name }}
        .text-lg.neutral700--text.ml-6 {{ stockTake.created | humanizeDatewithDay }}
      v-row.mt-6(no-gutters)
        v-col(cols="4")
          div.d-flex
            v-icon(color="neutral700" size="22") $yzi-users-solid-03
            div.text-sm.neutral700--text.ml-3 {{ stockTake.member.name }}
        v-col(offset="1" cols="4")
          div.d-flex
            v-icon(color="neutral700" size="22") $yzi-package-check-solid
            div.text-sm.neutral700--text.font-semibold.ml-3 Count:
            v-progress-circular.ml-4(v-if="isFetchingTotal" :width="2" :size="16" color="primary" indeterminate)
            div.text-sm.neutral700--text.ml-1(v-else) {{ changedProducts.length }} of {{ totalCount }} inventories counted

      v-row.mt-3(no-gutters)
        v-col(cols="4")
          div.d-flex
            v-icon(color="neutral700" size="22") $yzi-clock-check-solid
            div.text-sm.neutral700--text.font-semibold.ml-3 Time taken:
            div.text-sm.neutral700--text.ml-1 {{ getTimeDifference }}
        v-col(offset="1" cols="4")
          div.d-flex
            v-icon(color="neutral700" size="22") $yzi-calendar-solid
            div.text-sm.neutral700--text.font-semibold.ml-3 Start date and time:
            div.text-sm.neutral700--text.ml-1 {{ stockTake.created | convertServerDate2 }}
      v-row.mt-3(no-gutters)
        v-col(cols="4")
          div.d-flex
            v-icon(color="neutral700" size="22") $yzi-marker-pin-solid-04
            div.text-sm.neutral700--text.font-semibold.ml-3 Location involved{{ stockTake.summary && stockTake.summary.locations ? ` (${stockTake.summary.locations.length})` : '' }}:
              span.text-sm.font-normal.neutral700--text.ml-1(v-if="stockTake.summary && stockTake.summary.locations") {{ stockTake.summary.locations.join(', ') }}
              span.text-sm.font-normal.neutral700--text.ml-1(v-else) -
        v-col(offset="1" cols="4")
          div.d-flex
            v-icon(color="neutral700" size="22") $yzi-calendar-check-solid-01
            div.text-sm.neutral700--text.font-semibold.ml-3 End date and time:
            div.text-sm.neutral700--text.ml-1(v-if="stockTake.finished_at") {{ stockTake.finished_at | convertServerDate2 }}
            div.text-sm.neutral700--text.ml-1(v-else) -

      template(v-if="stockTake.summary")
        div.text-lg.font-semibold.neutral700--text.mt-6 Stock take summary
        v-card.round-xl.mt-3(outlined)
          v-simple-table
            thead
              tr
                th Type
                th Locations
                th Quantity
                th Value
            tbody
              tr
                td.text-sm.font-medium Current stock
                td.text-sm {{ stockTake.summary.locations.length ? stockTake.summary.locations.length > 1 ? `${stockTake.summary.locations.length} locations` : '1 location' : '-' }}
                td.text-sm {{ stockTake.summary.current_stock.quantity }}
                td.text-sm {{ stockTake.summary.current_stock.value | displayPrice($currency) }}
              tr
                td.text-sm.font-medium Actual stock
                td.text-sm {{ stockTake.summary.locations.length ? stockTake.summary.locations.length > 1 ? `${stockTake.summary.locations.length} locations` : '1 location' : '-' }}
                td.text-sm {{ stockTake.summary.actual_stock.quantity }}
                td.text-sm {{ stockTake.summary.actual_stock.value | displayPrice($currency) }}
              tr(style="background: var(--v-neutral200)")
                td.text-sm.font-semibold Difference
                td.text-sm
                td.text-sm.font-semibold {{ stockTake.summary.actual_stock.quantity - stockTake.summary.current_stock.quantity }}
                td.text-sm.font-semibold {{ (stockTake.summary.actual_stock.value - stockTake.summary.current_stock.value) | displayPrice($currency) }}
        
      v-card.round-xl.mt-8(outlined)
        div.d-flex.align-center.pa-4.clickable(@click="showItems = !showItems")
          div.text-lg.font-semibold {{ productCount }} items counted
          v-spacer
          v-icon(v-if="showItems") $yzi-chevron-up
          v-icon(v-else) $yzi-chevron-down
        .data-table(v-if="showItems")
          v-data-table.pb-2(:headers="headers" :items="products" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetchingProducts" :disable-sort="isMobileScreen" :header-props="{ sortIcon: '$yzi-triangle' }")
            //- item
            template(v-slot:item.name="{ item }")
              .text-sm.font-medium.neutral700--text {{ item.product.name }}
            //- uom
            template(v-slot:item.uom="{ item }")
              .text-sm.neutral700--text(v-if="getStockUnit(item)") {{ getStockUnit(item) }}
              .text-sm.neutral700--text(v-else) -
              .text-sm.neutral700--text(v-else) -
            //- current stock
            template(v-slot:item.inventory_qty="{ item }")
              .text-sm.font-medium.success500--text {{ item.balance - item.change }}
            //- actual stock
            template(v-slot:item.actual_stock="{ item }")
              .text-sm.font-medium.info500--text {{ item.balance }}
            //- stock difference
            template(v-slot:item.stock_difference="{ item }")
              .text-sm.font-semibold.neutral700--text {{ item.change }}

          div.d-flex.align-center.pa-4(v-if="productCount > pageSize")
            v-spacer
            yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="productCount" attach menu-top @input="pageChanged")
        
      div.py-6

    v-footer.white(app inset padless elevation="3")
      v-container.px-8.py-2(fluid)
        div.d-flex.align-center
          v-spacer
          y-button(label="Close" color="neutral700" dark :loading="isLoading" @click="closeStockTakeSummary")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  components: {},
  props: ['id', 'savePage', 'saveTab', 'saveSearch', 'saveFilters', 'listPage', 'clinicSlug'],
  data () {
    return {
      isLoading: false,
      isFetching: false,
      isFetchingProducts: false,
      isFetchingTotal: false,
      showItems: false,
      stockTake: null,
      pageSize: 20,
      productCount: 0,
      totalCount: 0,
      currentPage: 1,
      products: [],
      oriProducts: [],
      changedProducts: [],
      headers: [
        { text: 'Item', value: 'name', class: 'text-table-header' },
        { text: 'UOM', value: 'uom', class: 'text-table-header' },
        { text: 'Current stock', value: 'inventory_qty', class: 'text-table-header' },
        { text: 'Actual stock', value: 'actual_stock', class: 'text-table-header' },
        { text: 'Stock difference', value: 'stock_difference', class: 'text-table-header' }
      ],
    }
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    slug () {
      return this.clinicSlug || this.company.slug
    },
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
    getTimeDifference () {
      if (this.stockTake && this.stockTake.created && this.stockTake.finished_at) {
        const created = new Date(this.stockTake.created).getTime()
        const finished = new Date(this.stockTake.finished_at).getTime()
        const timeDifference = Math.abs(finished - created)
        const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24))
        const hours = Math.floor((timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60))

        let daysText = days ? days > 1 ? `${days} days ` : '1 day ' : ''
        let hoursText = hours ? hours > 1 ? `${hours} hours ` : '1 hour ' : ''
        let minutesText = minutes ? minutes > 1 ? `${minutes} minutes` : '1 minute' : ''

        return daysText + hoursText + minutesText
      }
      return '-'
    },
  },
  watch: {
    pageSize () {
      this.currentPage = 1
      this.fetchProducts()
    },
  },
  mounted () {
    this.fetchStockTake()
    this.fetchProductTotal()
  },
  methods: {
    getStockUnit (item) {
      if (item.uom_conversion_enabled && item.uom_conversions && item.uom_conversions.length) {
        return item.uom_conversions[item.uom_conversions.length - 1].convert_to || ''
      }
      return item.uom || ''
    },
    ...mapActions(['showMessage']),
    fetchStockTake () {
      this.isFetching = true
      this.$axios.get(`/clinics/${this.slug}/stock_takes/${this.id}`)
        .then(resp => {
          this.stockTake = resp.data
          this.oriProducts = JSON.parse(JSON.stringify(resp.data.products))
          this.changedProducts = JSON.parse(JSON.stringify(resp.data.products))
          this.fetchProducts()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    fetchProducts () {
      this.isFetchingProducts = true
      let params = {
        stock_take: this.stockTake.id,
        company: this.company.id,
        page: this.currentPage,
        page_size: this.pageSize
      }
      this.$axios.get(`/product_inventories`, { params })
        .then(resp => {
          this.products = resp.data.results
          this.productCount = resp.data.count
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingProducts = false
        })
    },
    fetchProductTotal () {
      this.isFetchingTotal = true
      let params = {
        status: 'published',
        page_size: 1,
        fields: 'id'
      }
      this.$axios.get(`/clinics/${this.slug}/inventories`, { params })
        .then(resp => {
          this.totalCount = resp.data.count
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingTotal = false
        })
    },
    pageChanged () {
      this.fetchProducts()
    },
    closeStockTakeSummary () {
      if (this.listPage) {
        let params = {
          savePage: this.savePage,
          saveTab: this.saveTab,
          saveSearch: this.saveSearch,
          saveFilters: this.saveFilters
        }
        this.$router.push({ name: this.listPage, params, query: this.$route.query })
      } else {
        window.location.href = 'https://yezza.com'
      }
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  } 
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
}
.empty-box {
  position: relative;
  border-radius: 10px;
  border: var(--v-basic500) dashed 2px;
}
.button-box {
  border-left: solid 1px var(--v-neutral400);
  border-top: solid 1px var(--v-neutral400);
  border-bottom: solid 1px var(--v-neutral400);
  border-radius: 10px 0 0 10px;
  background-color: var(--v-neutral200);
}
</style>